<template>
  <!-- TKP-208: çalışma alanı kütüphanesini Excel'den yükle — şablon, ön izleme (yazmaz), yükleme -->
  <div class="library-import" data-test="library-import">
    <p class="small text-muted mb-2">{{ $t("workspace.library.import.hint") }}</p>
    <div class="d-flex flex-wrap align-items-center mb-2">
      <b-button variant="light" size="sm" class="mr-2 mb-1" data-test="library-import-template" :disabled="busy" @click="downloadTemplate"><i class="bx bx-download"></i> {{ $t("workspace.library.import.template") }}</b-button>
      <!-- TKP-247: kütüphaneyi içe aktarma biçiminde (4 dil) indir — düzenleyip aynı ekrandan geri yüklenebilir -->
      <b-button v-for="t in exportTargets" :key="t.key" variant="light" size="sm" class="mr-2 mb-1" :data-test="`library-export-${t.key}`" :disabled="busy" @click="download(t.url, t.filename)"><i class="bx" :class="busy ? 'bx-loader bx-spin' : 'bx-spreadsheet'"></i> {{ $t(`workspace.library.export.${t.key}`) }}</b-button>
      <input ref="file" type="file" accept=".xlsx" class="form-control-file form-control-sm mb-1" style="max-width: 18rem" data-test="library-import-file" @change="onFile" />
    </div>
    <div class="d-flex mb-2">
      <b-button variant="outline-primary" size="sm" class="mr-2" data-test="library-import-preview" :disabled="busy || !file" @click="run(true)"><i class="bx" :class="busy ? 'bx-loader bx-spin' : 'bx-search-alt'"></i> {{ $t("workspace.library.import.preview") }}</b-button>
      <b-button variant="success" size="sm" data-test="library-import-apply" :disabled="busy || !preview || !preview.totals.created" @click="run(false)"><i class="bx bx-upload"></i> {{ $t("workspace.library.import.apply", { count: preview ? preview.totals.created : 0 }) }}</b-button>
    </div>

    <div v-if="report" class="small" data-test="library-import-report">
      <div class="mb-1" :class="report.dryRun ? 'text-muted' : 'text-success'">
        {{ $t(report.dryRun ? "workspace.library.import.preview-summary" : "workspace.library.import.done", report.totals) }}
      </div>
      <div v-if="report.unknownSheets && report.unknownSheets.length" class="text-warning mb-1" data-test="library-import-unknown">{{ $t("workspace.library.import.unknown-sheets", { names: report.unknownSheets.join(", ") }) }}</div>
      <table class="table table-sm table-bordered mb-1">
        <thead class="thead-light">
          <tr>
            <th>{{ $t("workspace.library.import.col-sheet") }}</th>
            <th class="text-right">{{ $t(report.dryRun ? "workspace.library.import.col-new" : "workspace.library.import.col-created") }}</th>
            <th class="text-right">{{ $t("workspace.library.import.col-existing") }}</th>
            <th class="text-right">{{ $t("workspace.library.import.col-problems") }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="r in rows">
            <tr :key="r.sheet" data-test="library-import-row">
              <td>{{ r.sheet }}</td>
              <td class="text-right">{{ r.created }}</td>
              <td class="text-right">{{ r.existing }}</td>
              <td class="text-right">
                <span v-if="r.problems.length" class="text-danger hand" @click="toggle(r.sheet)">{{ r.problems.length }} <i class="bx" :class="open[r.sheet] ? 'bx-chevron-up' : 'bx-chevron-down'"></i></span>
                <span v-else>0</span>
              </td>
            </tr>
            <tr v-if="open[r.sheet]" :key="r.sheet + '-p'">
              <td colspan="4">
                <div v-for="p in r.problems" :key="p.kind + p.row" :class="p.kind === 'error' ? 'text-danger' : 'text-muted'">{{ $t("workspace.library.import.row", { row: p.row }) }}: {{ p.message }}</div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import rest from "@/rest-client";
import { exportTargets, importPaths, reportRows, uploadForm } from "./library-import";

/** TKP-208 — iki yerde: yönetici paneli çalışma alanı formu (`mode="management"`) ve çalışma alanı sahibinin sayfası. */
export default {
  name: "library-import-panel",
  props: { mode: { type: String, default: "workspace" }, workspaceId: { type: String, default: null } },
  data() {
    return { file: null, busy: false, preview: null, report: null, open: {} };
  },
  computed: {
    paths() {
      return importPaths(this.mode);
    },
    rows() {
      return reportRows(this.report);
    },
    exportTargets() {
      return exportTargets(this.mode, this.workspaceId);
    },
  },
  methods: {
    onFile(event) {
      this.file = (event && event.target && event.target.files && event.target.files[0]) || null;
      this.preview = null;
      this.report = null;
    },
    toggle(sheet) {
      this.$set(this.open, sheet, !this.open[sheet]);
    },
    downloadTemplate() {
      return this.download(this.paths.template, "kutuphane-sablonu.xlsx");
    },
    async download(path, filename) {
      this.busy = true;
      try {
        const response = await axios.get(path, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.busy = false;
      }
    },
    /** Önce ön izleme (yazmaz); onayla yükleme — yüklemeden sonra rapor gerçek sayıları gösterir. */
    async run(dryRun) {
      if (!this.file) return;
      if (!dryRun && !(await this.confirm(this.$t("workspace.library.import.confirm", { count: this.preview ? this.preview.totals.created : 0 })))) return;
      this.busy = true;
      try {
        const { data } = await rest.post(this.paths.upload, uploadForm(this.file, { workspace: this.workspaceId, dryRun }));
        this.report = data;
        if (dryRun) this.preview = data;
        else {
          this.preview = null;
          this.$toast.success(this.$t("workspace.library.import.done", data.totals));
          this.$emit("imported", data);
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
